import {
	DATA_CLEAN_FILTERS_NUM,
	DATA_TYPES,
	EXTRA_HEADERS,
	FILTER_OPS_NUM,
} from "../../constants";
import { convertToNumber } from "../../helpers/DataHelpers";
import Column from "../../MachineLearning/Column";
import Dataset from "../../MachineLearning/Dataset";
import { cleanDataFilterType, datasetData } from "../../state";

export function makeCleanDataFilter(
	input: cleanDataFilterType
): cleanDataFilterType {
	return {
		column: input.column,
		operation: input.operation,
		value: input.value,
		replace_with: input.replace_with,
		drop_row: input.drop_row,
		index: input.index,
	};
}

function gt(a, b) {
	return convertToNumber(a) > convertToNumber(b);
}
function lt(a, b) {
	return convertToNumber(a) < convertToNumber(b);
}
function eq(a, b) {
	return a === b;
}
function neq(a, b) {
	return a !== b;
}

export function filterNameCheck(name: string) {
	let headers = Object.values(EXTRA_HEADERS).map((v) =>
		v.replace("{val}", "")
	);
	for (let header of headers) {
		if (header.startsWith(name)) {
			return true;
		}
	}
	return false;
}

export function areFiltersSame(
	f1In: cleanDataFilterType[],
	f2In: cleanDataFilterType[]
) {
	if (f1In.length !== f2In.length) {
		return false;
	}
	let f1 = f1In.map((v) => JSON.stringify(v));
	let f2 = f2In.map((v) => JSON.stringify(v));

	let f1Set = new Set(f1);
	let f2Set = new Set(f2);

	for (let val of f1) {
		if (!f2Set.has(val)) {
			return false;
		}
	}
	for (let val of f2) {
		if (!f1Set.has(val)) {
			return false;
		}
	}
	return true;
}

export function checkIfDuplicate(
	filters: cleanDataFilterType[],
	filter: cleanDataFilterType
) {
	let tmp = [...filters];
	for (let val of tmp) {
		val.index = undefined;
	}
	let filtersSet = filters.map((v) => JSON.stringify(v));
	let filterStr = JSON.stringify(filter);
	return filtersSet.includes(filterStr);
}

export function areDropRowsSame(s1: Object, s2: Object) {
	if (Object.keys(s1).length !== Object.keys(s2).length) {
		return false;
	}
	for (let i of Object.keys(s1)) {
		if (!(i in s2)) {
			return false;
		}
	}
	for (let i of Object.keys(s2)) {
		if (!(i in s1)) {
			return false;
		}
	}
	return true;
}

export function calcAutoFilters(dataset: Dataset) {
	let filters: cleanDataFilterType[] = [];
	for (let i = 0; i < dataset.columns.length; i++) {
		let col = dataset.columns[i];
		if (
			col.columnType !== DATA_TYPES.number ||
			col.columnName.startsWith("Sentiment:")
		) {
			continue;
		}
		for (let f of calcAutoFilterForCol(col, dataset.data[col.columnName])) {
			filters.push(f);
		}
	}
	for (let i = 0; i < filters.length; i++) {
		filters[i].index = i;
	}
	return filters;
}

function calcAutoFilterForCol(col: Column, data: string[]) {
	let numData = Column.removeNaNs(
		Column.processNumberColumn(data),
		col.colStats.median
	);

	const q25 = col.colStats.q25;
	const q75 = col.colStats.q75;
	const topCutoff = q75 + (q75 - q25) * 2;
	const bottomCutoff = q25 - (q75 - q25) * 2;

	let foundTop = false;
	let topReplace = -Infinity;
	for (let val of numData) {
		if (val <= topCutoff) {
			topReplace = Math.max(val, topReplace);
		}
		if (val > topCutoff) {
			foundTop = true;
		}
	}
	let foundBottom = false;
	let bottomReplace = Infinity;
	for (let val of numData) {
		if (val >= bottomCutoff) {
			bottomReplace = Math.min(val, bottomReplace);
		}
		if (val < bottomCutoff) {
			foundBottom = true;
		}
	}

	let filters: cleanDataFilterType[] = [];
	if (foundTop) {
		let filter = makeCleanDataFilter({
			column: col.columnName,
			operation: FILTER_OPS_NUM.greater,
			value: topReplace + "",
			replace_with: topReplace + "",
			drop_row: false,
			index: undefined,
		});

		filters.push(filter);
	}
	if (foundBottom) {
		let filter = makeCleanDataFilter({
			column: col.columnName,
			operation: FILTER_OPS_NUM.less,
			value: bottomCutoff + "",
			replace_with: bottomReplace + "",
			drop_row: false,
			index: undefined,
		});
		filters.push(filter);
	}

	return filters;
}

function getFilterFn(filter: cleanDataFilterType) {
	let fn = undefined;
	if (filter.operation === DATA_CLEAN_FILTERS_NUM.equal) {
		fn = eq;
	} else if (filter.operation === DATA_CLEAN_FILTERS_NUM.greater) {
		fn = gt;
	} else if (filter.operation === DATA_CLEAN_FILTERS_NUM.less) {
		fn = lt;
	} else if (filter.operation === DATA_CLEAN_FILTERS_NUM.not_equal) {
		fn = neq;
	}
	return fn;
}

export function filterColumns(
	columns: Column[],
	origData: datasetData,
	filters: cleanDataFilterType[],
	rowsToDrop: Set<number>,
	modified?: Object
) {
	if (modified === undefined) {
		modified = {};
	}
	for (let i = 0; i < columns.length; i++) {
		let col = columns[i];

		let { data, valuesModified } = applyFiltersToData(
			origData,
			col.columnName,
			filters,
			rowsToDrop,
			modified
		);
		modified = valuesModified;
		columns[i] = new Column(
			data,
			col.columnName,
			undefined,
			col.availableColumnTypes
		);
		columns[i].colStats = col.colStats;
	}
	return { columns, modified };
}

export function applyFiltersToDash(
	dataset: Dataset,
	filters: cleanDataFilterType[],
	rowsToDrop: Object = {}
) {
	if (filters.length === 0) {
		return { dataset, modified: { "": undefined } };
	}
	let modified = {};
	for (let i = 0; i < dataset.columns.length; i++) {
		let col = dataset.columns[i];

		let { data, valuesModified } = applyFiltersToData(
			dataset.data,
			col.columnName,
			filters,
			rowsToDrop,
			modified
		);
		modified = valuesModified;
		dataset.columns[i] = new Column(data, col.columnName, col);
	}
	dataset.nRows = dataset.columns[0].vectorizedData.length;
	return { dataset, modified };
}

export function addFilterDropRows(
	origData: datasetData,
	filters: cleanDataFilterType[],
	rowsToDrop: Object
) {
	let dropRowFilters = filters.filter((f) => f.drop_row);

	for (let filter of dropRowFilters) {
		let fn = getFilterFn(filter);
		let data = origData[filter.column];
		for (let i = 0; i < data.length; i++) {
			if (fn(data[i], filter.value)) {
				rowsToDrop[i + 1] = 0;
			}
		}
	}
	return rowsToDrop;
}

export function applyFiltersToData(
	origDataMap: datasetData,
	colName: string,
	filters: cleanDataFilterType[],
	rowsToDrop: Object,
	valuesModified: Object = {}
) {
	let origData = origDataMap[colName];

	let rowsToDropFull = addFilterDropRows(origDataMap, filters, {
		...rowsToDrop,
	});
	let data: string[] = [];
	for (let i = 1; i <= origData.length; i++) {
		if (!(i in rowsToDropFull)) {
			data.push(origData[i - 1]);
		}
	}

	let replaceFilters = filters.filter((f) => !f.drop_row);
	replaceFilters = filters.filter((f) => f.column === colName);
	for (let filter of replaceFilters) {
		let fn = getFilterFn(filter);
		for (let i = 0; i < data.length; i++) {
			if (fn(data[i], filter.value)) {
				data[i] = filter.replace_with;
				valuesModified[`${i}_${colName}`] = 0;
			}
		}
	}
	return { data, valuesModified, rowsToDropFull };
}
